import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { withFormik, FormikProps } from 'formik';
import {
  Grid,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

import { translate } from 'helpers/intl';
import messages from 'containers/PasswordRecoveryPages/PinMethodForgotPassword/messages';
import CodeInput from 'components/CodeInput';
import { OrangeButton, PrimaryButton } from 'components/Button';
import {
  AlignedGrid,
  ErrorCaption,
  StyledGrid,
  StyledBox,
  PositionedShortBody,
} from 'containers/PasswordRecoveryPages/PinMethodForgotPassword/style';
import { Captions } from 'components/Typography';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { EVENT_CATEGORIES } from 'helpers/GATracking';
import { useDispatch } from 'react-redux';
import { setToasterOpen } from 'store/global/actions';

const InnerForm = (props: FormProps & FormikProps<FormValues>) => {
  const {
    values,
    errors,
    handleSubmit,
    isLoading,
    setFieldValue,
    proceedDifferently,
    errorMessage,
    hashedEmail,
    hashedPhone,
    showEmailOption,
    forgetPasswordEmail,
    showQuestionOption,
  } = props;

  const handlePinChange = (value: string) => {
    setFieldValue('pin', value);
  };
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <form onSubmit={handleSubmit}>
      <Box mb={8} paddingX={[2, 4, 8]}>
        <Grid container direction="column" justify="center">
          <Grid>
            <PositionedShortBody weight="500">
              {showEmailOption ? hashedEmail : hashedPhone}
            </PositionedShortBody>
          </Grid>
          <StyledGrid xs item dir="ltr">
            <CodeInput
              name="pin"
              handleChange={handlePinChange}
              value={values.pin}
              isValid={!errors.pin}
              fields={8}
              width={matches ? '26px' : '52px'}
              height={matches ? '28px' : '62px'}
            />

            <StyledBox marginTop={1}>
              {(errorMessage === 'Pin is not correct' || errors.pin) && (
                <ErrorCaption weight="300" color="#F36142">
                  {translate(messages.validationErrorMessage, props)}
                </ErrorCaption>
              )}
            </StyledBox>
          </StyledGrid>

          <Grid item xs>
            <GaClickHOC
              category={EVENT_CATEGORIES.FORGOTTEN_PASSWORD_EMAIL}
              label="valider"
            >
              <OrangeButton type="submit" variant="contained">
                {isLoading ? (
                  <CircularProgress data-testid="loader" size={20} />
                ) : (
                  <React.Fragment>
                    {translate(messages.valid, props)}
                  </React.Fragment>
                )}
              </OrangeButton>
            </GaClickHOC>
          </Grid>
          {showEmailOption && (
            <AlignedGrid item xs>
              <Captions
                onClick={
                  /* istanbul ignore next */ () => {
                    dispatch(
                      setToasterOpen({
                        title: 'newEmailSent',
                        severity: 'info',
                      }),
                    );
                    forgetPasswordEmail && forgetPasswordEmail();
                  }
                }
                weight="300"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {translate(messages.notYetReceived, props)}
              </Captions>
            </AlignedGrid>
          )}
          {showQuestionOption && (
            <Grid style={{ marginTop: '8px' }} item xs>
              <GaClickHOC
                category={EVENT_CATEGORIES.FORGOTTEN_PASSWORD_EMAIL}
                label="Procéder autrement"
              >
                <PrimaryButton variant="outlined" onClick={proceedDifferently}>
                  {translate(messages.proceedDifferently, props)}{' '}
                </PrimaryButton>
              </GaClickHOC>
            </Grid>
          )}
        </Grid>
      </Box>
    </form>
  );
};

export interface FormProps {
  intl: IntlShape;
  isLoading: Boolean;
  proceedDifferently: Function;
  onSubmitStep: Function;
  hashedEmail: string;
  hashedPhone: string;
  showEmailOption: boolean;
  showQuestionOption?: boolean;
  errorMessage: string;
  forgetPasswordEmail?: Function;
}

interface FormValues {
  pin: string;
}

const Form = withFormik<FormProps, FormValues>({
  mapPropsToValues: () => {
    return {
      pin: '',
    };
  },

  handleSubmit: ({ pin }, { props, setFieldError }) => {
    if (pin.length !== 8) {
      return setFieldError('pin', 'error');
    }

    return props.onSubmitStep(pin);
  },
})(InnerForm);

export default injectIntl(Form);
